import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ComparisonDashboardEnhanced from '../analysis-results-dashboard/components/ComparisonDashboardEnhanced';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ComparisonService from '../../services/comparisonService';
import MatchScoreCard from '../analysis-results-dashboard/components/MatchScoreCard';
import SkillsSpiderChart from '../analysis-results-dashboard/components/SkillsSpiderChart';
import KeywordAnalysis from '../analysis-results-dashboard/components/KeywordAnalysis';
import SkillGapAnalysis from '../analysis-results-dashboard/components/SkillGapAnalysis';
import ActionPanel from '../analysis-results-dashboard/components/ActionPanel';
import UpgradeCTA from './components/UpgradeCTA';
import ContextDensityChecker from './components/ContextDensityChecker';
import ExternalLink from '../../components/ui/ExternalLink';
import FeedbackWidget from '../../components/feedback/FeedbackWidget';

const toTitle = (value, fallback = 'Skill gap') =>
  (value || fallback)
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

const safeArray = (value) => (Array.isArray(value) ? value : []);

const proficiencyToScore = (level) => {
  const map = { expert: 95, advanced: 85, intermediate: 70, beginner: 50 };
  return map[level] ?? 65;
};

const AnalysisResultsLive = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enhancedComparison, setEnhancedComparison] = useState(null);
  const [scanDates, setScanDates] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);



  useEffect(() => {
    const loadEnhanced = async () => {
      // PRIORITY 1: Check if comparison was passed in navigation state (from fresh re-scan)
      if (state?.enhanced_comparison) {
        console.log('📊 Using comparison from navigation state');
        setEnhancedComparison(state.enhanced_comparison);
        return; // Use state data, don't query database
      }

      // PRIORITY 2: Load from database if not in state
      if (state?.analysisId) {
        const { data } = await supabase
          .from('resume_analyses')
          .select('enhanced_comparison, previous_scan_id, created_at')
          .eq('id', state.analysisId)
          .single();

        if (data?.enhanced_comparison) {
          setEnhancedComparison(data.enhanced_comparison);
          if (data.previous_scan_id) {
            const { data: prev } = await supabase
              .from('resume_analyses')
              .select('created_at')
              .eq('id', data.previous_scan_id)
              .single();
            if (prev) {
              setScanDates({
                previous: prev.created_at,
                daysBetween: Math.floor((new Date(data.created_at) - new Date(prev.created_at)) / 86400000)
              });
            }
          }
        }
      }
    };
    loadEnhanced();
  }, [state?.analysisId, state?.enhanced_comparison]);

  if (!state?.analysis) {
    navigate('/resume-upload-analysis');
  }

  const { jobPosting, analysis, paymentStatus } = state;
  const isFreeTier = paymentStatus?.tier === 'free';

  const safeStr = (v, fallback = '') => (typeof v === 'string' ? v : fallback);

  const jobTitle = safeStr(jobPosting?.title);
  const company = safeStr(jobPosting?.company);
  const locationText = safeStr(jobPosting?.location);
  const sourceDomain = safeStr(jobPosting?.source_domain, 'updated just now');

  const groupedSkills = useMemo(() => {
    return safeArray(analysis.skills_found_grouped).map((group, groupIndex) => {
      const category = group?.category || `Category ${groupIndex + 1}`;
      const summary = typeof group?.summary === 'string' ? group.summary : '';
      const skills = safeArray(group?.skills).map((skill, skillIndex) => {
        if (typeof skill === 'string') {
          return {
            name: skill,
            category,
            proficiency: 'intermediate',
            confidence_score: 0.7,
            years_experience: 0,
            evidence: '',
            aliases: []
          };
        }

        return {
          name: skill?.name || skill?.skill || `Skill ${skillIndex + 1}`,
          category: skill?.category || category,
          proficiency: (skill?.proficiency || skill?.level || 'intermediate').toLowerCase(),
          confidence_score:
            typeof skill?.confidence_score === 'number'
              ? skill.confidence_score
              : typeof skill?.confidence === 'number'
                ? skill.confidence
                : typeof skill?.score === 'number'
                  ? Math.min(Math.max(skill.score / 100, 0), 1)
                  : 0.7,
          years_experience: Number.isFinite(skill?.years_experience)
            ? Number(skill.years_experience)
            : Number.isFinite(skill?.experience_years)
              ? Number(skill.experience_years)
              : 0,
          evidence: skill?.evidence || skill?.resume_evidence || skill?.context || '',
          aliases: safeArray(skill?.aliases)
        };
      });

      return {
        category,
        summary,
        skills
      };
    });
  }, [analysis.skills_found_grouped]);

  const normalizedSkillsFound = useMemo(() => {
    if (groupedSkills.length > 0) {
      return groupedSkills.flatMap((group) =>
        group.skills.map((skill) => ({
          skill_name: skill.name,
          proficiency_level: skill.proficiency,
          category: skill.category,
          confidence_score: skill.confidence_score,
          evidence: skill.evidence
        }))
      );
    }

    return safeArray(analysis.skills_found).map((skill, index) => {
      if (typeof skill === 'string') {
        return {
          skill_name: skill,
          proficiency_level: 'intermediate',
          category: 'Skill',
          confidence_score: 0.7
        };
      }
      return {
        skill_name: skill?.name || skill?.skill || `Skill ${index + 1}`,
        proficiency_level: (skill?.proficiency || skill?.level || 'intermediate').toLowerCase(),
        category: skill?.category || 'Skill',
        confidence_score:
          typeof skill?.confidence_score === 'number'
            ? skill.confidence_score
            : typeof skill?.confidence === 'number'
              ? skill.confidence
              : typeof skill?.score === 'number'
                ? Math.min(Math.max(skill.score / 100, 0), 1)
                : 0.75
      };
    });
  }, [analysis.skills_found, groupedSkills]);

  const skillsRadarData = useMemo(() => {
    const radar = [];

    normalizedSkillsFound.slice(0, 2).forEach((skill) => {
      radar.push({
        skill: skill.skill_name,
        candidate_score: proficiencyToScore(skill.proficiency_level),
        market_demand: 80,
        justification: `Strength: ${skill.skill_name}`
      });
    });

    safeArray(analysis.skills_missing)
      .slice(0, 2)
      .forEach((gap) => {
        radar.push({
          skill: gap.name,
          candidate_score: 40,
          market_demand: Number.isFinite(gap.market_demand) ? Math.round(gap.market_demand) : 90,
          justification: `Gap: ${gap.reason || 'Build this capability'}`
        });
      });

    return radar.slice(0, 4);
  }, [analysis.skills_missing, normalizedSkillsFound]);

  const normalizedAnalysisData = useMemo(
    () => ({
      overall_score: analysis.overall_resume_score || 0,
      job_match_score: analysis.job_match_score || 0,
      extracted_skills: normalizedSkillsFound,
      skills_match: safeArray(analysis.skills_matching || analysis.skills_matching_detailed).map(
        (item) => (typeof item === 'string' ? item : item?.name || '')
      ),
      missing_skills: safeArray(analysis.skills_missing).map((item) => item.name),
      job_matches: jobPosting
        ? [
          {
            id: 'live-role',
            job_title: jobPosting.title,
            company: jobPosting.company,
            external_url: jobPosting.job_url
          }
        ]
        : [],
      resume_keywords: normalizedSkillsFound.map((skill) => skill.skill_name)
    }),
    [analysis, jobPosting, normalizedSkillsFound]
  );

  const keywordAnalysisData = useMemo(() => {
    const found = normalizedSkillsFound.map((skill) => ({
      name: skill.skill_name,
      category: skill.category || 'Skill',
      frequency: Math.round((skill.confidence_score || 0.7) * 100),
      strength: (skill.proficiency_level || 'intermediate').toLowerCase(),
      evidence: skill.evidence
    }));

    const missing = safeArray(analysis.skills_missing).map((item) => ({
      name: item.name,
      category: toTitle(item.reason, 'High-impact gap'),
      impact: (item.severity || 'recommended').toLowerCase(),
      marketDemand: Number.isFinite(item.market_demand)
        ? Math.round(item.market_demand)
        : undefined,
      note: item.reason
    }));

    return { found, missing };
  }, [analysis.skills_missing, normalizedSkillsFound]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
          <button onClick={() => navigate('/user-dashboard')} className="text-sm text-primary hover:underline">
            ← Back to Dashboard
          </button>
          <div className="sm:text-right">
            <h1 className="text-base sm:text-lg font-semibold text-foreground">Your Job Fit Analysis</h1>
            <p className="text-xs text-muted-foreground">
              Powered by Experts and AI
            </p>
          </div>
        </div>
      </header>

      {/* Warning Banner: Storage Failed */}
      {state?._storageWarning && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-6">
            <div className="flex items-start gap-4">
              <span className="text-3xl">⚠️</span>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-destructive mb-2">
                  Analysis Not Saved - Please Scan Again
                </h3>
                <p className="text-sm text-foreground mb-3">
                  Your analysis completed successfully, but we couldn't save it to your account
                  due to a temporary issue. <strong>Your credit was NOT deducted</strong>, so you
                  haven't lost anything.
                </p>
                <p className="text-sm text-foreground mb-4">
                  You can review the results below for reference, but they won't appear in your
                  dashboard or history. <strong>Please upload your resume again</strong> to save
                  the analysis properly.
                </p>
                <button
                  onClick={() => navigate('/resume-upload-analysis')}
                  className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors text-sm font-medium"
                >
                  📄 Scan Again (No Credit Used)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <section className="bg-card border border-border rounded-lg p-4 sm:p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Job Snapshot</h2>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Title</p>
                  <p className="font-medium text-foreground">{jobTitle}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Company</p>
                  <p className="font-medium text-foreground">{company}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Location</p>
                  <p className="font-medium text-foreground">{locationText}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Source</p>
                  {jobPosting.job_url ? (
                    <a
                      href={jobPosting.job_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      {jobPosting.source_domain || 'View listing'}
                    </a>
                  ) : (
                    <span className="font-medium text-foreground">Captured via search</span>
                  )}
                </div>
              </div>
              {jobPosting.summary && (
                <p className="mt-4 text-sm text-foreground whitespace-pre-line">{jobPosting.summary}</p>
              )}
            </section>

            {/* Enhanced Comparison Dashboard - Shows delta-first insights for re-scans */}
            {enhancedComparison && (
              <ComparisonDashboardEnhanced
                enhancedComparison={enhancedComparison}
                scanDates={scanDates}
              />
            )}

            <MatchScoreCard
              overallScore={analysis.overall_resume_score}
              analysisData={normalizedAnalysisData}
              role={jobTitle}
            />

            {/* Upgrade CTA Banner for Free Users */}
            {isFreeTier && (
              <UpgradeCTA
                message="Upgrade to see full analysis. Note: You'll need to re-scan after upgrading as we use advanced models in paid tier."
                variant="banner"
                ctaText="Upgrade / Top Up Credits"
              />
            )}

            {isFreeTier ? (
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Resume Skills by Category</h2>
                <UpgradeCTA message="Upgrade to see detailed skill analysis extracted from your resume." />
              </section>
            ) : (
              groupedSkills.length > 0 && (
                <section className="bg-card border border-border rounded-lg p-6 space-y-5">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Resume Skills by Category</h2>
                    <p className="text-sm text-muted-foreground">
                      These skills were extracted directly from your resume and organised to highlight strengths across different domains.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {groupedSkills.map((group) => (
                      <details key={group.category} className="group bg-muted/30 rounded-lg">
                        <summary className="flex items-center justify-between px-4 py-3 cursor-pointer">
                          <div>
                            <p className="font-semibold text-foreground">{group.category}</p>
                            {group.summary && (
                              <p className="text-xs text-muted-foreground mt-1">{group.summary}</p>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground group-open:hidden">
                            {group.skills.length} skills
                          </span>
                        </summary>
                        <div className="px-4 pb-4 space-y-2">
                          {group.skills.map((skill) => (
                            <div
                              key={skill.name}
                              className="flex flex-wrap items-center justify-between gap-2 text-sm bg-background border border-border/50 rounded-md px-3 py-2"
                            >
                              <div>
                                <p className="font-medium text-foreground">{skill.name}</p>
                                {skill.evidence && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {skill.evidence}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                  {skill.category}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {toTitle(skill.proficiency)} ·{' '}
                                  {Math.round((skill.confidence_score || 0) * 100)}% confidence
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )
            )}

            {/* E. Conditionally show SkillsSpiderChart and KeywordAnalysis */}
            {isFreeTier ? (
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Skills Analysis</h2>
                <UpgradeCTA message="Upgrade to see visual skills comparison and keyword analysis." />
              </section>
            ) : (
              <>
                <SkillsSpiderChart
                  extractedSkills={normalizedAnalysisData.extracted_skills}
                  missingSkills={normalizedAnalysisData.missing_skills}
                  skillsRadar={skillsRadarData}
                  role={jobTitle}
                />

                <KeywordAnalysis
                  foundSkills={keywordAnalysisData.found}
                  missingSkills={keywordAnalysisData.missing}
                />
              </>
            )}

            {/* F. Conditionally show Skill Development Roadmap */}
            <section className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Skill Development Roadmap</h2>
              {isFreeTier ? (
                <UpgradeCTA message="Upgrade to see personalized learning paths with courses and YouTube resources for each skill gap." />
              ) : (
                <div className="space-y-6">
                  {safeArray(analysis.skill_roadmap).length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No roadmap items were generated for this analysis. Paste a richer job description or rerun the scan to receive targeted learning steps.
                    </p>
                  )}
                  {safeArray(analysis.skill_roadmap).map((item, idx) => (
                    <div
                      key={`${item.skill}-${idx}`}
                      className="border border-border rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-lg font-semibold text-foreground">
                            {idx + 1}. {item.skill}
                          </p>
                          <p className="text-xs uppercase text-muted-foreground">
                            {toTitle(
                              safeArray(analysis.skills_missing).find(
                                (missing) => missing.name.toLowerCase() === item.skill.toLowerCase()
                              )?.severity,
                              'Recommended'
                            )}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.why_it_matters}
                      </p>

                      {item.premium_course?.url && (
                        <div>
                          <p className="text-sm font-medium text-foreground mb-2">Premium Course</p>
                          <ul className="space-y-2 text-sm">
                            <li>
                              <a
                                className="text-primary underline"
                                href={item.premium_course.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {item.premium_course.title || 'Recommended course'}
                              </a>
                              <span className="text-muted-foreground ml-2">
                                [{toTitle(item.premium_course.provider || 'Course')}]
                              </span>
                            </li>
                          </ul>
                        </div>
                      )}

                      {/* Unified YouTube videos (no Top/Rising labels) */}
                      {(() => {
                        const vids = [
                          ...(Array.isArray(item.youtube_videos) ? item.youtube_videos : []),
                          ...(item.youtube_top && item.youtube_top.url ? [item.youtube_top] : []),
                          ...(item.youtube_affiliate && item.youtube_affiliate.url ? [item.youtube_affiliate] : []),
                        ];

                        return vids.length > 0 ? (
                          <div>
                            <p className="text-sm font-medium text-foreground mb-2">YouTube Videos</p>
                            <ul className="space-y-2 text-sm">
                              {vids.map((v, i) => (
                                <li key={i} className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="font-medium truncate">{v.title || 'YouTube video'}</div>
                                    <div className="text-muted-foreground truncate">
                                      {v.channel || ''}{v.subscriber_count ? ` • ${v.subscriber_count}` : ''}
                                    </div>
                                  </div>
                                  <a
                                    className="shrink-0 text-xs px-2 py-1 rounded-md border border-border hover:bg-muted transition-colors"
                                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent((v.title || 'tutorial') + ' ' + (v.channel || ''))}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Watch
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Re-scan Comparison Analysis */}
            {enhancedComparison && !isFreeTier && (
              <ComparisonDashboardEnhanced
                comparison={enhancedComparison}
                scanDates={scanDates}
              />
            )}

            {/* Show banner if this is a re-scan but comparison failed to generate */}
            {state?.analysisId && state?.analysis?.previous_scan_id && !enhancedComparison && !isFreeTier && (
              <section className="bg-warning/10 border border-warning rounded-lg p-6 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-warning mb-2">
                      Enhanced Comparison Insights Unavailable
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Your analysis and basic comparison data were saved successfully.
                      The enhanced AI insights (progress summary, skill gap changes, roadmap updates)
                      could not be generated at this time.
                    </p>
                    <button
                      onClick={async () => {
                        if (!user?.id) return;
                        setIsRegenerating(true);
                        try {
                          console.log('🔄 Regenerating enhanced comparison...');
                          const comparison = await ComparisonService.generateEnhancedComparison(
                            user.id,
                            state.analysisId
                          );

                          if (comparison) {
                            await supabase
                              .from('resume_analyses')
                              .update({ enhanced_comparison: comparison })
                              .eq('id', state.analysisId);

                            setEnhancedComparison(comparison);
                            console.log('✅ Comparison regenerated successfully');
                          } else {
                            alert('No comparison data was generated. This might not be a re-scan.');
                          }
                        } catch (err) {
                          console.error('❌ Failed to regenerate:', err);
                          alert('Failed to generate insights. Please try again later or contact support.');
                        } finally {
                          setIsRegenerating(false);
                        }
                      }}
                      disabled={isRegenerating}
                      className="px-4 py-2 bg-warning text-warning-foreground rounded-md hover:bg-warning/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRegenerating ? '⏳ Generating...' : '🔄 Try Generating Insights Again'}
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* G. Conditionally show Additional Resources */}
            {isFreeTier ? (
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Additional Helpful Resources</h2>
                <UpgradeCTA message="Upgrade to see curated YouTube resources and training programs." />
              </section>
            ) : (
              safeArray(analysis.additional_resources).length > 0 && (
                <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Additional Helpful Resources</h2>
                    <p className="text-sm text-muted-foreground">
                      Curated YouTube resources for further study.
                    </p>
                  </div>
                  <ul className="space-y-3 text-sm">
                    {analysis.additional_resources.map((res, resIdx) => (
                      <li
                        key={`${res.url || res.title}-${resIdx}`}
                        className="bg-muted/30 rounded-lg px-3 py-2 flex items-center justify-between"
                      >
                        <div>
                          <a className="text-primary underline" href={`https://www.youtube.com/results?search_query=${encodeURIComponent((res.title || 'tutorial') + ' ' + (res.channel || ''))}`} target="_blank" rel="noopener noreferrer">
                            {res.title || 'YouTube video'}
                          </a>
                          <span className="text-muted-foreground ml-2">
                            [{res.channel}{res.subscriber_count ? ` • ${res.subscriber_count}` : ''}]
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>


                </section>
              )
            )}

            {/* H. Conditionally show Resume Tips */}
            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold text-foreground">Resume Tips (tailored for this job)</h2>
              {isFreeTier ? (
                <UpgradeCTA message="Upgrade to see actionable resume rewrites with before/after examples." />
              ) : (
                <>
                  {safeArray(analysis.resume_tips).length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No resume tips were generated. Try rerunning the analysis with more detailed project descriptions.
                    </p>
                  )}
                  {safeArray(analysis.resume_tips).map((tip, index) => (
                    <div
                      key={`${tip.section || 'general'}-${index}`}
                      className="border border-border rounded-lg p-4 space-y-2"
                    >
                      <p className="text-xs font-semibold text-primary uppercase tracking-wide">
                        {toTitle(tip.section)}
                      </p>

                      {tip.original_text ? (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-semibold">Original: </span>
                          <span className="italic">{tip.original_text}</span>
                        </div>
                      ) : null}

                      <div className="text-sm text-foreground">
                        <span className="font-semibold">Rewrite: </span>
                        {tip.rewritten_suggestion || tip.suggestion}
                      </div>

                      {tip.reasoning ? (
                        <p className="text-xs text-muted-foreground">
                          Reason: {tip.reasoning}
                        </p>
                      ) : null}

                      {tip.example_line && (
                        <p className="text-sm text-muted-foreground italic">Example: {tip.example_line}</p>
                      )}
                    </div>
                  ))}
                </>
              )}
            </section>
          </div>

          <div className="space-y-6">
            {/* Conditionally show SkillGapAnalysis */}
            {isFreeTier ? (
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Skill Gap Analysis</h2>
                <UpgradeCTA message="Upgrade to see detailed skill gaps and personalized recommendations." />
              </section>
            ) : (
              <SkillGapAnalysis
                skillRoadmap={analysis.skill_roadmap}
                skillsMissing={analysis.skills_missing}
              />
            )}

            {/* Recruiter's Eye View i.e. Context & Density Checker */}
            {isFreeTier ? (
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Recruiter's Eye View</h2>
                <UpgradeCTA message="Upgrade to see which skills need better contextualization in your experience sections." />
              </section>
            ) : (
              <ContextDensityChecker
                contextDensity={analysis.context_density}
                isFreeTier={isFreeTier}
              />
            )}

            {/* I. Conditionally show Interview Prep in right column */}
            {isFreeTier ? (
              <section className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Hyper-Contextual Interview Prep</h2>
                <UpgradeCTA message="Upgrade to see personalized interview questions tailored to your resume and this role." />
              </section>
            ) : (
              Array.isArray(analysis.interview_prep) && analysis.interview_prep.length > 0 && (
                <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                  <h2 className="text-xl font-semibold text-foreground">Hyper-Contextual Interview Prep</h2>
                  <p className="text-sm text-muted-foreground">
                    Practice answers tailored to your resume and this role.
                  </p>
                  <div className="space-y-4">
                    {analysis.interview_prep.map((q, i) => (
                      <div key={i} className="border border-border rounded-lg p-4 space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          Question {i + 1}: {q.question}
                        </p>
                        {q.reason_for_question ? (
                          <p className="text-xs text-muted-foreground">
                            Why this is asked: {q.reason_for_question}
                          </p>
                        ) : null}
                        {Array.isArray(q.suggested_answer_points) && q.suggested_answer_points.length > 0 ? (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-1">Answer points:</p>
                            <ul className="list-disc ml-5 text-sm space-y-1">
                              {q.suggested_answer_points.map((p, j) => (
                                <li key={j} className="text-foreground break-words">{p}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              )
            )}

            <ActionPanel
              analysis={analysis}
              jobPosting={jobPosting}
            />

            {/* Custom Training Resources */}

            <section className="bg-card border border-border rounded-lg p-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Recommended Training Programs</h2>
                <p className="text-sm text-muted-foreground">
                  Curated upskilling cohorts and communities to fast-track your next role.
                </p>
              </div>
              <ul className="space-y-4 text-sm">
                {/* EnterpriseAIFoundry */}
                <li className="bg-muted/30 rounded-lg px-4 py-3">
                  <div className="font-medium text-foreground mb-1">EnterpriseAIFoundry</div>
                  <p className="text-muted-foreground text-xs mb-2">
                    Learn Enterprise AI skills (A must for everyone now)
                  </p>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>• Build & Ship Enterprise AI/GenAI Projects</p>
                    <p>• Get Job-Ready in Weeks not Months</p>
                  </div>
                  <ExternalLink
                    href="https://qubitlyventures.com/EnterpriseAIFoundry"
                    className="text-primary underline mt-2 inline-block"
                  >
                    Learn more →
                  </ExternalLink>
                </li>

                {/* AIFoundry */}
                <li className="bg-muted/30 rounded-lg px-4 py-3">
                  <div className="font-medium text-foreground mb-1">AIFoundry</div>
                  <p className="text-muted-foreground text-xs mb-2">
                    For ambitious founders (why not build yourself)
                  </p>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>• From Idea to Invested Company</p>
                    <p>• We provide the infrastructure, and expert guidance. You bring the vision and the drive</p>
                  </div>
                  <ExternalLink
                    href="https://aibrahmi.com/aifoundry"
                    className="text-primary underline mt-2 inline-block"
                  >
                    Learn more →
                  </ExternalLink>
                </li>

                {/* AI for Everyone */}
                <li className="bg-muted/30 rounded-lg px-4 py-3">
                  <div className="font-medium text-foreground mb-1">AI for Everyone</div>
                  <p className="text-muted-foreground text-xs mb-2">
                    AI is not only for engineers
                  </p>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>• This is the course for everyone - especially non-technical candidates</p>
                  </div>
                  <ExternalLink
                    href="https://qubitlyventures.com/ai-for-all"
                    className="text-primary underline mt-2 inline-block"
                  >
                    Learn more →
                  </ExternalLink>
                </li>

                {/* AIBrahmi */}
                <li className="bg-muted/30 rounded-lg px-4 py-3">
                  <div className="font-medium text-foreground mb-1">AIBrahmi</div>
                  <p className="text-muted-foreground text-xs mb-2">
                    Navigate the world of AI with free self training platform
                  </p>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>• Find expert sessions and events</p>
                    <p>• Explore all AI articles and Research Papers</p>
                  </div>
                  <ExternalLink
                    href="https://aibrahmi.com/"
                    className="text-primary underline mt-2 inline-block"
                  >
                    Explore platform →
                  </ExternalLink>
                </li>
              </ul>
            </section>
          </div>
        </div>

        {/* Feedback Widget - Placed at the end of results */}
        <div className="mt-8">
          <FeedbackWidget
            analysisId={state?.analysisId}
            pageContext="analysis-results-live"
          />
        </div>
      </main>
    </div>
  );
};

export default AnalysisResultsLive;